import { motion } from "framer-motion";
import { cn } from "@/lib/cn";
import { CategoryBadge } from "@/components/sartn/CategoryBadge";
import type { ContactCategory } from "@/components/sartn/CategoryBadge";

interface ContactType {
  id: number;
  firstName: string;
  lastName: string | null;
  username: string | null;
  phone: string | null;
  category: ContactCategory;
  isBlocked: boolean;
  lastMessageAt: string | null;
  messageCount: number;
}

interface ContactCardProps {
  contact: ContactType;
  onClick?: () => void;
}

function formatRelativeTime(dateString: string): string {
  const now = Date.now();
  const then = new Date(dateString).getTime();
  const diffMs = now - then;
  const diffMinutes = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMinutes < 1) return "Now";
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;

  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function getInitial(name: string): string {
  return name.charAt(0).toUpperCase();
}

function getAvatarColor(name: string): string {
  const colors = [
    "bg-blue-600",
    "bg-emerald-600",
    "bg-purple-600",
    "bg-amber-600",
    "bg-rose-600",
    "bg-cyan-600",
    "bg-indigo-600",
    "bg-teal-600",
  ];
  let hash = 0;
  for (const char of name) {
    hash = char.charCodeAt(0) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length]!;
}

export function ContactCard({ contact, onClick }: ContactCardProps) {
  const fullName = contact.lastName
    ? `${contact.firstName} ${contact.lastName}`
    : contact.firstName;

  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileTap={{ scale: 0.98 }}
      className={cn(
        "flex w-full items-center gap-3 rounded-[var(--radius-lg)] p-3",
        "bg-[var(--color-surface)] shadow-sm",
        "text-left transition-colors",
        "active:bg-[var(--color-border-subtle)]",
      )}
    >
      <div
        className={cn(
          "flex h-11 w-11 shrink-0 items-center justify-center rounded-full",
          "text-sm font-semibold text-white",
          getAvatarColor(contact.firstName),
        )}
      >
        {getInitial(contact.firstName)}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-semibold text-[var(--color-text)]">
            {fullName}
          </span>
          {contact.isBlocked && (
            <span className="shrink-0 rounded-[var(--radius-full)] bg-red-50 px-2 py-0.5 text-[10px] font-medium text-red-700">
              Blocked
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <CategoryBadge category={contact.category} size="sm" />
          {contact.messageCount > 0 && (
            <span className="text-xs text-[var(--color-text-muted)]">
              {contact.messageCount} messages
            </span>
          )}
        </div>
      </div>

      {contact.lastMessageAt && (
        <span className="shrink-0 text-[11px] text-[var(--color-text-muted)]">
          {formatRelativeTime(contact.lastMessageAt)}
        </span>
      )}
    </motion.button>
  );
}

export type { ContactType };
